import React from 'react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import EventCard from '@/components/layout/EventCard';
import { OrganizerDetailViewProps } from './organizerdetailpage.props';

const OrganizerDetailView: React.FC<OrganizerDetailViewProps> = ({
  organizer,
  organizerEvents,
  loading,
  navigate,
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Skeleton className="h-10 w-24 mb-6" />
          <Card className="mb-12">
            <CardContent className="p-8">
              <div className="flex items-start space-x-6">
                <Skeleton className="w-24 h-24 rounded-full" />
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-8 w-1/2" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-6 w-1/4" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Skeleton className="h-8 w-64 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!loading && !organizer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Организаторот не е пронајден</h2>
          <Button onClick={() => navigate('/organizers')}>Врати се</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>

        <Card className="mb-12">
          <CardContent className="p-8">
            <div className="flex items-start space-x-6">
              <img src={organizer.logo} alt={organizer.name} className="w-24 h-24 rounded-full object-cover" />
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-3">{organizer.name}</h1>
                <p className="text-gray-700 mb-4">{organizer.description}</p>
                <a
                  href={organizer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Посетете го нивниот сајт
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Настани од овој организатор</h2>
        {organizerEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organizerEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-12">Нема настани од овој организатор.</p>
        )}
      </div>
    </div>
  );
};

export default OrganizerDetailView;
